/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { ThemeProvider } from './components/ThemeProvider';
import { PushNotificationModal } from './components/PushNotificationModal';
import { OfflineBanner } from './components/OfflineBanner';
import { InstallPopup } from './components/InstallPopup';
import { GoogleAnalytics } from './components/GoogleAnalytics';
import { Toaster, toast } from 'react-hot-toast';
import { db } from './lib/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { Bell } from 'lucide-react';

import { Home } from './pages/Home';

const PostDetail = lazy(() => import('./pages/PostDetail').then(module => ({ default: module.PostDetail })));
const Search = lazy(() => import('./pages/Search').then(module => ({ default: module.Search })));
const Bookmarks = lazy(() => import('./pages/Bookmarks').then(module => ({ default: module.Bookmarks })));
const Category = lazy(() => import('./pages/Category').then(module => ({ default: module.Category })));
const PageDetail = lazy(() => import('./pages/PageDetail').then(module => ({ default: module.PageDetail })));
const UrlResolver = lazy(() => import('./pages/UrlResolver').then(module => ({ default: module.UrlResolver })));
const Shorts = lazy(() => import('./pages/Shorts').then(module => ({ default: module.Shorts })));
const WebStoriesPlayer = lazy(() => import('./pages/WebStoriesPlayer').then(module => ({ default: module.WebStoriesPlayer })));
const Archive = lazy(() => import('./pages/Archive'));
const EPaper = lazy(() => import('./pages/EPaper'));
const AdminApp = lazy(() => import('./admin/AdminApp').then(module => ({ default: module.AdminApp })));


export default function App() {
  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', 'notifications'), (snapshot) => {
       if (snapshot.exists()) {
          const list = snapshot.data().notifications;
          if (Array.isArray(list) && list.length > 0) {
             const latest = list[0];
             const lastId = localStorage.getItem('last_notified_id');
             if (latest.id && latest.id !== lastId) {
                // Initialize last notified ID on first load to prevent old notification spam
                if (!lastId) {
                   localStorage.setItem('last_notified_id', latest.id);
                   return;
                }
                
                localStorage.setItem('last_notified_id', latest.id);
                
                // HTML5 Notification permission trigger
                if ('Notification' in window && Notification.permission === 'granted') {
                   try {
                      new Notification(latest.title, {
                         body: latest.body,
                         icon: '/pwalogo.png',
                         tag: latest.id
                      });
                   } catch (e) {
                      console.error('Failed to fire Native notification', e);
                   }
                }

                // Push Beautiful In-App Hot Toast Alert
                toast.custom((t) => (
                   <div 
                      key={latest.id}
                      className={`${t.visible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'} transition-all duration-300 max-w-sm w-full bg-[#E53935] text-white shadow-2xl rounded-2xl pointer-events-auto flex p-4 items-center gap-3 cursor-pointer border border-red-700 font-bengali`}
                      onClick={() => {
                         if (latest.url) {
                            window.location.href = latest.url;
                         }
                         toast.dismiss(t.id);
                      }}
                   >
                      <div className="bg-white/20 p-2.5 rounded-full text-white shrink-0">
                         <Bell className="w-6 h-6 animate-bounce" />
                      </div>
                      <div className="flex-1 overflow-hidden">
                         <span className="text-[10px] bg-white text-[#E53935] px-2 py-0.5 rounded-full font-extrabold uppercase">ব্রেকিং নিউজ</span>
                         <h4 className="font-bold text-sm mt-1 leading-snug truncate">{latest.title}</h4>
                         <p className="text-xs text-white/90 line-clamp-2 mt-0.5 leading-relaxed">{latest.body}</p>
                      </div>
                   </div>
                ), { duration: 8000, position: 'top-right' });
             }
          }
       }
    });

    return () => unsub();
  }, []);

  return (
    <ThemeProvider>
      <BrowserRouter>
          <GoogleAnalytics />
          <OfflineBanner />
          <PushNotificationModal />
          <Toaster position="bottom-right" />
          <InstallPopup />
          <Suspense fallback={<div className="flex h-screen items-center justify-center dark:bg-gray-900"><div className="w-8 h-8 md:w-10 md:h-10 border-4 border-red-500 border-t-transparent rounded-full animate-spin"></div></div>}>
            <Routes>
              <Route path="/admin/*" element={<AdminApp />} />
              <Route path="/" element={<Home />} />
              <Route path="/explore" element={<Navigate to="/" replace />} /> {/* Redirect explore to home for now */}
              <Route path="/search" element={<Search />} />
              <Route path="/shorts" element={<Shorts />} />
              <Route path="/stories" element={<WebStoriesPlayer />} />
              <Route path="/stories/:id" element={<WebStoriesPlayer />} />
              <Route path="/bookmarks" element={<Bookmarks />} />
              <Route path="/category/:catId" element={<Category />} />
              <Route path="/post/:id" element={<PostDetail />} />
              <Route path="/p/:slug" element={<PageDetail />} />
              <Route path="/archive" element={<Archive />} />
              <Route path="/epaper" element={<EPaper />} />
              <Route path="/:year/:month/:slug" element={<UrlResolver />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
    </ThemeProvider>
  );
}

